/**
 * Knowing when the theme changed.
 *
 * **Why this exists.** Almost every surface in AEGIS is styled by CSS, so a
 * theme change is free: swap the tokens and the browser repaints. The chart is
 * the exception. Lightweight Charts draws to a canvas and takes literal colour
 * strings, so its colours are READ from the token layer once, when the chart is
 * created — which means a runtime theme switch leaves a dark chart sitting on a
 * white page. It did exactly that, and this is the fix.
 *
 * Two sources have to be watched, because the theme has three states:
 *
 * - An explicit choice sets `data-theme` on `<html>`, so a mutation observer
 *   catches it.
 * - The default sets nothing and follows the OS, so `prefers-color-scheme`
 *   has to be watched too — a user changing their system theme at sunset gets
 *   the same stranded chart otherwise.
 */

import { cssVar, dark } from '@aegis/ui';
import type { SemanticToken } from '@aegis/ui';

export type ThemeListener = () => void;

const listeners = new Set<ThemeListener>();

function notify(): void {
  for (const l of listeners) l();
}

let started = false;

function start(): void {
  if (started || typeof window === 'undefined') return;
  started = true;

  // An explicit choice: `data-theme` on the root element.
  new MutationObserver(notify).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme'],
  });

  // The default: no attribute at all, following the OS.
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', notify);
}

/**
 * Run `fn` whenever the theme changes. Returns an unsubscribe.
 *
 * Deliberately not a React hook: the chart's listener has to live inside the
 * same effect that owns the chart instance, and a hook would re-subscribe on
 * every render of a component that renders on every tick.
 */
export function onThemeChange(fn: ThemeListener): () => void {
  start();
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Read one semantic token as a literal.
 *
 * The only sanctioned way for canvas code to learn a colour. A component that
 * hard-codes one instead is invisible to the redesign pass — see docs/18 §2.
 *
 * **The fallback is not passed in.** It used to be, and that meant twenty-four
 * hand-typed hex strings scattered through the canvas components — a third copy
 * of the palette, after the CSS and the generated TypeScript, and the only one
 * nothing checked. It came from `@aegis/ui` now, so a colour changed in
 * `semantic.css` cannot leave a stale literal behind in a fallback.
 *
 * The fallback only applies when the custom property is missing entirely — SSR,
 * or a stylesheet that failed to load. Dark is the right choice there because it
 * is the default theme.
 */
export function token(name: SemanticToken): string {
  if (typeof window === 'undefined') return dark[name];
  return resolve(cssVar(name)) || dark[name];
}

/**
 * Make the browser resolve a token to a real colour.
 *
 * **Why this exists.** Every themed token is now one `light-dark(light, dark)`
 * declaration. `getPropertyValue('--surface-base')` returns the custom
 * property's *specified* value — the literal string `"light-dark(#FBFAF7,
 * #0B0F15)"` — because `light-dark()` is resolved at use, against the element's
 * `color-scheme`. Handing that string to a canvas or to Lightweight Charts
 * silently paints nothing.
 *
 * So the token is applied to a throwaway element as a real `color`, and the
 * *computed* colour is read back. That is the browser doing the resolution,
 * which means it accounts for the theme in force — including "System" following
 * the OS — with no second implementation of that decision living here.
 *
 * **A fresh element per read, and that is not wasteful — it is the fix.** The
 * first version kept one probe and re-assigned `color` on it. Chrome does not
 * recompute a `var()` reference on re-assignment within the same task, so a
 * burst of reads all returned the FIRST token's colour. `Chart.tsx` makes about
 * fifteen calls in a row on mount, which painted the pane in the market-up
 * colour and drew every candle in the background colour. Fresh elements cost a
 * few DOM nodes on mount and on theme change, which is when this runs.
 *
 * **The sentinel goes inside `var()`, as its fallback.** `color: var(--nope)` is
 * syntactically valid whether or not the property exists, so the declaration
 * always sticks; when the property is missing it is invalid at computed-value
 * time and `color` falls back to the INHERITED colour, not to anything we set.
 * `var(--token, <sentinel>)` is the only form that tells the two apart.
 */
function resolve(prop: string): string {
  const probe = document.createElement('span');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText =
    'position:fixed;width:0;height:0;overflow:hidden;clip-path:inset(50%);pointer-events:none';
  probe.style.color = `var(${prop}, ${SENTINEL})`;
  // Inside documentElement rather than body so it inherits the same
  // `color-scheme` the tokens are declared against.
  document.documentElement.appendChild(probe);
  const got = getComputedStyle(probe).color;
  probe.remove();
  return got === SENTINEL ? '' : got;
}

/**
 * A colour no token uses and nothing would paint with, so matching it means the
 * lookup failed rather than that the token happens to be this value. Written in
 * the form `getComputedStyle` returns, so the comparison needs no normalising.
 */
const SENTINEL = 'rgb(1, 2, 3)';

/**
 * Read one *metric* token as a literal.
 *
 * Separate from `token()` on purpose. Metric tokens are per-platform — mobile
 * brings its own scale and does not import `metrics.web.css` — so there is no
 * shared palette to fall back to and the caller has to supply one. Canvas needs
 * exactly one of these today (the mono font stack), and the friction of a second
 * function is the point: it makes reaching for a metric a visible decision
 * rather than an accident. See docs/18 §7.
 */
export function metric(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}
